import axios from 'axios';
import { getApiUrl } from '../config/apiUrl';

import {
  createDecipheriv,
  createCipheriv,
  randomBytes,
} from 'crypto-browserify';

/**
 * Muestra un toast temporal en la página actual
 * @param {string} message - Mensaje a mostrar
 * @param {string} severity - Severidad del toast ('error', 'warning', 'info', 'success')
 */
function showTemporaryToast(message, severity = 'error') {
  // Verificar si ya se mostró este toast para evitar duplicados
  const toastKey = `toast_${message.substring(0, 50)}`;
  const lastShown = sessionStorage.getItem(toastKey);
  const now = Date.now();
  
  // Si se mostró hace menos de 2 segundos, no mostrar de nuevo
  if (lastShown && (now - parseInt(lastShown)) < 2000) {
    return;
  }
  
  // Marcar que se mostró este toast
  sessionStorage.setItem(toastKey, now.toString());
  
  // Crear contenedor de toast si no existe
  let toastContainer = document.getElementById('global-toast-container');
  if (!toastContainer) {
    toastContainer = document.createElement('div');
    toastContainer.id = 'global-toast-container';
    toastContainer.style.cssText = `
      position: fixed;
      top: 20px;
      left: 50%;
      transform: translateX(-50%);
      z-index: 9999;
      pointer-events: none;
    `;
    document.body.appendChild(toastContainer);
  }

  // Crear el toast
  const toast = document.createElement('div');
  toast.style.cssText = `
    background-color: ${severity === 'error' ? '#d32f2f' : severity === 'warning' ? '#ed6c02' : severity === 'info' ? '#1976d2' : '#2e7d32'};
    color: #ffffff;
    padding: 14px 20px;
    borderRadius: 12px;
    boxShadow: 0px 8px 24px rgba(0, 0, 0, 0.4);
    marginBottom: 10px;
    minWidth: 300px;
    maxWidth: 600px;
    fontSize: 1rem;
    fontWeight: 600;
    display: flex;
    alignItems: center;
    justifyContent: space-between;
    pointer-events: auto;
    animation: slideDown 0.3s ease-out;
  `;

  // Agregar animación CSS si no existe
  if (!document.getElementById('toast-animations')) {
    const style = document.createElement('style');
    style.id = 'toast-animations';
    style.textContent = `
      @keyframes slideDown {
        from {
          opacity: 0;
          transform: translateY(-20px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
      @keyframes slideUp {
        from {
          opacity: 1;
          transform: translateY(0);
        }
        to {
          opacity: 0;
          transform: translateY(-20px);
        }
      }
    `;
    document.head.appendChild(style);
  }

  toast.textContent = message;

  // Agregar botón de cerrar
  const closeButton = document.createElement('button');
  closeButton.textContent = '×';
  closeButton.style.cssText = `
    background: none;
    border: none;
    color: #ffffff;
    fontSize: 24px;
    cursor: pointer;
    marginLeft: 12px;
    padding: 0;
    width: 24px;
    height: 24px;
    display: flex;
    alignItems: center;
    justifyContent: center;
    borderRadius: 50%;
    transition: background-color 0.2s;
  `;
  closeButton.onmouseover = () => {
    closeButton.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
  };
  closeButton.onmouseout = () => {
    closeButton.style.backgroundColor = 'transparent';
  };
  closeButton.onclick = () => {
    toast.style.animation = 'slideUp 0.3s ease-out';
    setTimeout(() => {
      if (toast.parentNode) {
        toast.parentNode.removeChild(toast);
      }
    }, 300);
  };

  toast.appendChild(closeButton);
  toastContainer.appendChild(toast);

  // Auto-ocultar después de 6 segundos
  setTimeout(() => {
    if (toast.parentNode) {
      toast.style.animation = 'slideUp 0.3s ease-out';
      setTimeout(() => {
        if (toast.parentNode) {
          toast.parentNode.removeChild(toast);
        }
      }, 300);
    }
  }, 6000);
}

/**
 * Redirige al login con un mensaje de error en la URL
 * @param {string} message - Mensaje de error a mostrar
 */
export function redirectToLoginWithMessage(message) {
  // Importar dinámicamente para evitar dependencias circulares
  const { getRouteWithSlug } = require('../utils/routeUtils');
  
  // Mostrar toast antes de redirigir
  showTemporaryToast(message, 'error');
  
  // Esperar un momento para que el usuario vea el toast antes de redirigir
  setTimeout(() => {
    // Preservar el slug antes de limpiar
    const { getCompanySlugFromUrl, saveCompanySlug } = require('../utils/companyDetector');
    const slugToPreserve = getCompanySlugFromUrl();
    
    // Limpiar sesión
    localStorage.removeItem('cea');
    localStorage.removeItem('aec');
    
    // Restaurar el slug después de limpiar
    if (slugToPreserve) {
      saveCompanySlug(slugToPreserve);
    }
    
    // Codificar el mensaje para la URL
    const encodedMessage = encodeURIComponent(message || 'Acceso denegado. La empresa no tiene una suscripción activa.');
    
    // Redirigir al login con el mensaje como parámetro, preservando el slug
    const loginRoute = getRouteWithSlug('/auth/login');
    window.location.href = `${loginRoute}?error=${encodedMessage}`;
  }, 500);
}

/**
 * The `callApi` function is an asynchronous function that makes an HTTP request to a specified URL
 * with a specified method, token, and body, and returns the response data.
 * @param url - The `url` parameter is the endpoint URL where the API request will be sent.
 * @param method - The `method` parameter is the HTTP method to be used for the API call, such as
 * "GET", "POST", "PUT", "DELETE", etc.
 * @param token - The `token` parameter is a string that represents the authentication token used for
 * authorization. It is optional and can be provided if the API requires authentication.
 * @param body - The `body` parameter is the data that you want to send in the request body. It should
 * be an object that will be converted to JSON before sending the request.
 * @returns The function `callApi` returns a Promise that resolves to the response from the API call.
 */
/* export const callApi = async (url, method, token, body) => {
  // console.log('CallApi', url);
  const localToken = validateToken(localStorage.getItem('session'));
  
  try {
    if (process.env.REACT_APP_AAA === 'true') {
      body = { data: encryptedaes256cbc(body, localToken) };
    }
  } catch (error) {
    // console.log("EREA", error);
  }

  const responsecall = await axios({
    method: method,
    url: url,
    headers: {
      Authorization: token ? 'Bearer ' + token : '',
      'Content-Type': 'application/json',
    },
    data: JSON.stringify(body),
  }).catch((error) => {
    if (error.response && error.response.status === 401) {
      // Handle 401 response here
      return { status: false };
    } else {
      // Handle other errors
      return { status: false, session: false };
    }
  });

  if (!responsecall.status) {
    return responsecall;
  } else {
    const json = responsecall.data;
    if (process.env.REACT_APP_AAA === 'true' && json.data && localToken) {
      try {
        return await descryptedaes256cbc(json.data, localToken);
      } catch (error) {
        console.error('Error desencriptando respuesta:', error);
        return json;
      }
    }

    return json;
  }
}; */

export const callApi = async (url, method, token, body = null) => {
  const headers = {
    'Content-Type': 'application/json',
  };

  // Solo agregar Authorization si hay token
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  // Si es superadmin y tiene una empresa seleccionada, agregar companyId a la URL
  let finalUrl = url;
  if (token) {
    try {
      const companyId = getApiCompanyId();
      if (companyId !== null && isSuperAdmin()) {
        const urlObj = new URL(url, window.location.origin);
        urlObj.searchParams.set('companyId', companyId.toString());
        finalUrl = urlObj.toString();
      }
    } catch (error) {
      // Si hay error al parsear la URL, usar la URL original
      console.warn('Error al agregar companyId a la URL:', error);
    }
  }

  const options = {
    method,
    headers,
  };

  // Solo agregar body si existe
  if (body) {
    options.body = JSON.stringify(body);
  }

  try {
  const response = await fetch(finalUrl, options);
    
    // Si la respuesta no es OK, manejar diferentes tipos de errores
  if (!response.ok) {
      // Si es 403 (Forbidden) por suscripción inactiva
      if (response.status === 403) {
        let errorData;
        try {
          errorData = await response.json();
        } catch (e) {
          errorData = { message: 'Acceso denegado' };
        }
        
        // Extraer el mensaje correctamente (puede estar anidado)
        let errorMessage = errorData?.message;
        if (!errorMessage && errorData?.error) {
          // Si el mensaje está en error.error (string JSON anidado)
          try {
            const nestedError = typeof errorData.error === 'string' ? JSON.parse(errorData.error) : errorData.error;
            errorMessage = nestedError?.message || errorData.message;
          } catch (e) {
            errorMessage = errorData.message;
          }
        }
        
        errorMessage = errorMessage || 'Acceso denegado. La empresa no tiene una suscripción activa. Por favor, contacte al administrador.';
        
        // Si no es una llamada de login/auth, redirigir inmediatamente
        const isAuthCall = finalUrl.includes('/auth/');
        if (!isAuthCall && token) {
          // Redirigir al login con el mensaje
          redirectToLoginWithMessage(errorMessage);
          // Lanzar error para detener la ejecución
          const error = new Error(JSON.stringify(errorData));
          error.status = 403;
          error.data = { ...errorData, message: errorMessage };
          throw error;
        }
        
        // Si es una llamada de auth, solo lanzar el error
        const error = new Error(JSON.stringify(errorData));
        error.status = 403;
        error.data = { ...errorData, message: errorMessage };
        throw error;
      }
      
      // Si es 401 (Unauthorized), intentar refrescar el token
      if (response.status === 401 && token) {
        // Intentar refrescar el token
        const refreshed = await refreshAccessToken();
        if (refreshed) {
          // Reintentar la petición con el nuevo token
          // Recalcular finalUrl por si el companyId cambió después del refresh
          let retryFinalUrl = finalUrl;
          try {
            const companyId = getApiCompanyId();
            if (companyId !== null && isSuperAdmin()) {
              const urlObj = new URL(url, window.location.origin);
              urlObj.searchParams.set('companyId', companyId.toString());
              retryFinalUrl = urlObj.toString();
            }
          } catch (error) {
            // Si hay error, usar la URL original
            console.warn('Error al recalcular URL para reintento:', error);
          }
          
          headers.Authorization = `Bearer ${refreshed}`;
          const retryOptions = {
            ...options,
            headers: { ...headers },
          };
          const retryResponse = await fetch(retryFinalUrl, retryOptions);
          if (retryResponse.ok) {
            return await retryResponse.json();
          }
          // Si después de refrescar sigue fallando, verificar si es 403
          if (retryResponse.status === 403) {
            const errorData = await retryResponse.json().catch(() => ({ message: 'Acceso denegado' }));
            const error = new Error(JSON.stringify(errorData));
            error.status = 403;
            error.data = errorData;
            throw error;
          }
          // Si sigue siendo 401 u otro error, lanzar el error
          const retryErrorData = await retryResponse.json().catch(() => ({ message: 'Error en la petición' }));
          const retryError = new Error(JSON.stringify(retryErrorData));
          retryError.status = retryResponse.status;
          retryError.data = retryErrorData;
          throw retryError;
        }
      }
      
      const errorData = await response.json().catch(() => ({ message: 'Error en la petición' }));
      
      // Extraer el mensaje correctamente del errorData
      let errorMessage = errorData?.message || errorData?.error || 'Error en la petición';
      
      // Si el mensaje es un JSON string, parsearlo para extraer el mensaje real
      if (typeof errorMessage === 'string' && errorMessage.trim().startsWith('{')) {
        try {
          const parsed = JSON.parse(errorMessage);
          errorMessage = parsed.message || parsed.error || errorMessage;
        } catch (e) {
          // Si no se puede parsear, usar el mensaje tal cual
        }
      }
      
      // Crear un error con el mensaje extraído (no el JSON completo)
      const error = new Error(errorMessage);
      error.status = response.status;
      error.data = { ...errorData, message: errorMessage };
      throw error;
    }

    // Verificar si la respuesta tiene contenido antes de parsear JSON
    const contentType = response.headers.get('content-type');
    const text = await response.text();
    
    // Si no hay contenido, retornar un objeto vacío o de éxito
    if (!text || text.trim() === '') {
      return { success: true };
    }
    
    // Si el contenido es JSON, parsearlo
    if (contentType && contentType.includes('application/json')) {
      try {
        const parsed = JSON.parse(text);
        // Si es un número primitivo, retornarlo directamente
        if (typeof parsed === 'number') {
          return parsed;
        }
        return parsed;
      } catch (parseError) {
        console.error('Error parseando JSON:', parseError, 'Response text:', text);
        // Si el texto es solo un número, retornarlo como número
        const numText = text.trim();
        if (/^\d+$/.test(numText)) {
          return parseInt(numText, 10);
        }
        return { success: true, message: text || 'Operación completada' };
      }
    }
    
    // Si no es JSON pero es un número, retornarlo como número
    const numText = text.trim();
    if (/^\d+$/.test(numText)) {
      return parseInt(numText, 10);
    }
    
    // Si no es JSON, retornar el texto como mensaje
    return { success: true, message: text };
  } catch (error) {
    console.error('Error en callApi:', error);
    throw error;
  }
};

/**
 * Refresca el access token usando el refresh token guardado
 */
async function refreshAccessToken() {
  try {
    // Intentar desencriptar primero, luego JSON (compatibilidad hacia atrás)
    let tokenData = null;
    const ceaData = localStorage.getItem('cea');
    if (!ceaData) {
      return null;
    }

    try {
      // Primero intentar desencriptar
      const localToken = getTokenView(localStorage.getItem('session'));
      if (localToken) {
        tokenData = descryptedaes256cbc(ceaData, localToken);
      }
    } catch (error) {
      // Si falla desencriptar, intentar JSON (compatibilidad hacia atrás)
      try {
        tokenData = JSON.parse(ceaData);
      } catch (jsonError) {
        console.error('Error leyendo token:', jsonError);
        return null;
      }
    }

    if (!tokenData || !tokenData.refresh_token) {
      return null;
    }

    const response = await fetch(`${getApiUrl()}/api/v3/auth/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        refresh_token: tokenData.refresh_token,
      }),
    });

    if (response.ok) {
      const newTokenData = await response.json();
      
      // Actualizar el token en localStorage (encriptado)
      const updatedTokenData = {
        ...tokenData,
        access_token: newTokenData.access_token,
        refresh_token: newTokenData.refresh_token || tokenData.refresh_token,
      };
      const localToken = getTokenView(localStorage.getItem('session'));
      localStorage.setItem('cea', encryptedaes256cbc(updatedTokenData, localToken));
      
      // Disparar evento personalizado para notificar a los componentes que el token se refrescó
      window.dispatchEvent(new CustomEvent('tokenRefreshed', { 
        detail: { access_token: newTokenData.access_token } 
      }));
      
      return newTokenData.access_token;
    }
  } catch (error) {
    console.error('Error al refrescar token:', error);
    // Si falla el refresh, limpiar sesión
    localStorage.removeItem('cea');
    localStorage.removeItem('aec');
    window.location.href = '/auth/login';
  }
  return null;
}

export const callSendFornData = async (url, token, formData) => {
  const makeRequest = async (authToken) => {
    const config = {
      headers: {
        Authorization: authToken ? 'Bearer ' + authToken : '',
        'Content-Type': 'multipart/form-data', // Esencial para enviar FormData
      },
    };

    return await axios.post(url, formData, config);
  };

  try {
    let response = await makeRequest(token);
    const json = response.data;
    
    // La respuesta ya viene sin encriptar
    return json;
  } catch (error) {
    // Si es 401 (Unauthorized), intentar refrescar el token
    if (error.response && error.response.status === 401 && token) {
      const refreshed = await refreshAccessToken();
      if (refreshed) {
        // Reintentar la petición con el nuevo token
        try {
          const retryResponse = await makeRequest(refreshed);
          return retryResponse.data;
        } catch (retryError) {
          console.error('Error al subir la foto después de refrescar token:', retryError);
          throw retryError;
        }
      }
    }
    console.error('Error al subir la foto:', error);
    throw error;
  }
};

/**
 * The function `callApiInit` is an asynchronous function that makes an API call using the specified
 * URL, method, token, and body, and returns the response data as JSON.
 * @param url - The `url` parameter is the endpoint URL where the API request will be sent.
 * @param method - The `method` parameter is the HTTP method to be used for the API call, such as
 * 'GET', 'POST', 'PUT', 'DELETE', etc.
 * @param token - The `token` parameter is a string that represents an authentication token. It is used
 * to authorize the API request. If a token is provided, it will be included in the request headers as
 * a bearer token. If no token is provided, the Authorization header will be empty.
 * @param body - The `body` parameter is the data that you want to send in the request body. It should
 * be an object that will be converted to JSON using `JSON.stringify()` before sending the request.
 * @returns the JSON response from the API call.
 */
const INIT_TIMEOUT_MS = 15000;
const INIT_MAX_ATTEMPTS = 3;

export const callApiInit = async (url, method, token, body) => {
  const headers = {};
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  // Sin body no se envía Content-Type: así el GET es una petición simple y el
  // navegador se ahorra el preflight OPTIONS antes de poder arrancar la app.
  const hasBody = body !== null && body !== undefined;
  if (hasBody) {
    headers['Content-Type'] = 'application/json';
  }

  let lastError;
  for (let attempt = 1; attempt <= INIT_MAX_ATTEMPTS; attempt++) {
    try {
      const responsecall = await axios({
        method,
        url,
        headers,
        timeout: INIT_TIMEOUT_MS,
        ...(hasBody ? { data: JSON.stringify(body) } : {}),
      });

      return responsecall.data;
    } catch (error) {
      lastError = error;

      // Si el servidor respondió, el reintento no cambia nada.
      if (error.response) {
        throw error;
      }

      if (attempt < INIT_MAX_ATTEMPTS) {
        await new Promise((resolve) => setTimeout(resolve, 400 * 2 ** (attempt - 1)));
      }
    }
  }

  throw lastError;
};

/**
 * The function `callApiLogin` is used to make an API call for logging in, with optional encryption and
 * decryption of the request and response data.
 * @param url - The `url` parameter is the URL of the API endpoint that you want to call. It specifies
 * the location where the API request should be sent.
 * @param method - The `method` parameter is the HTTP method to be used for the API call, such as
 * "GET", "POST", "PUT", "DELETE", etc.
 * @param token - The `token` parameter is a string representing an authentication token. It is used to
 * authorize the API request. If a token is provided, it will be included in the request headers as a
 * Bearer token. If no token is provided, the Authorization header will be empty.
 * @param body - The `body` parameter is an object that contains the following properties:
 * @returns the response data from the API call. If the `REACT_APP_AAA` environment variable is set to
 * `'true'`, the response data is decrypted using the `descryptedaes256cbc` function before being
 * returned.
 */
export const callApiLogin = async (url, method, token, body, additionalHeaders = {}) => {
  try {
    // Enviar datos directamente sin encriptar
    const responsecall = await axios({
      method: method,
      url: url,
      headers: {
        Authorization: token ? 'Bearer ' + token : '',
        'Content-Type': 'application/json',
        ...additionalHeaders,
      },
      data: JSON.stringify(body),
    });

    const json = responsecall.data;
    
    // La respuesta ya viene sin encriptar
    return json;
  } catch (error) {
    // Manejar errores de axios
    if (error.response) {
      // El servidor respondió con un código de estado fuera del rango 2xx
      let errorMessage = error.response.data?.message || error.response.data?.error || 'Error en la petición';
      
      // Si el error es un string JSON, intentar parsearlo para extraer el mensaje completo
      if (typeof errorMessage === 'string' && errorMessage.trim().startsWith('{')) {
        try {
          const parsedError = JSON.parse(errorMessage);
          // Extraer el mensaje del objeto parseado, buscando en diferentes niveles
          if (parsedError.message) {
            // Si el mensaje contiene "An error occurred during sign-in:", extraer solo la parte después
            if (typeof parsedError.message === 'string' && parsedError.message.includes('An error occurred during sign-in:')) {
              const parts = parsedError.message.split('An error occurred during sign-in:');
              errorMessage = parts.length > 1 ? parts[1].trim() : parsedError.message;
            } else {
              errorMessage = parsedError.message;
            }
          } else if (parsedError.error) {
            errorMessage = parsedError.error;
          }
        } catch (e) {
          // Si no se puede parsear, buscar el mensaje dentro del string usando regex
          const messageMatch = errorMessage.match(/"message"\s*:\s*"((?:[^"\\]|\\.)*)"/);
          if (messageMatch && messageMatch[1]) {
            let extractedMessage = messageMatch[1].replace(/\\"/g, '"').replace(/\\n/g, '\n').replace(/\\t/g, '\t');
            // Si el mensaje contiene "An error occurred during sign-in:", extraer solo la parte después
            if (extractedMessage.includes('An error occurred during sign-in:')) {
              const parts = extractedMessage.split('An error occurred during sign-in:');
              extractedMessage = parts.length > 1 ? parts[1].trim() : extractedMessage;
            }
            errorMessage = extractedMessage;
          }
        }
      }
      
      const errorData = {
        status: error.response.status,
        statusCode: error.response.status,
        message: errorMessage,
        data: error.response.data,
      };
      const newError = new Error(JSON.stringify(errorData));
      newError.status = error.response.status;
      newError.data = errorData;
      throw newError;
    } else if (error.request) {
      // La petición fue hecha pero no se recibió respuesta
      throw new Error('No se pudo conectar con el servidor');
    } else {
      // Algo pasó al configurar la petición
      throw error;
    }
  }
};

export const callApiRegister = async (url, method, token, body) => {
  // Enviar datos directamente sin encriptar
  const responsecall = await axios({
    method: method,
    url: url,
    headers: {
      Authorization: token ? 'Bearer ' + token : '',
      'Content-Type': 'application/json',
    },
    data: JSON.stringify(body),
  });

  const json = responsecall.data;

  // La respuesta ya viene sin encriptar
  return json;
};

export function descryptedaes256cbc(encryptedText, privateKey) {
  // Validar que encryptedText no sea null o undefined
  if (!encryptedText || !privateKey) {
    throw new Error('encryptedText o privateKey no pueden ser null o undefined');
  }

  try {
    const encryptedArray = encryptedText.split(':');
    if (encryptedArray.length !== 2) {
      throw new Error('Formato de texto encriptado inválido');
    }
    const iv = Buffer.from(encryptedArray[0], 'hex');
    const encryptedData = Buffer.from(encryptedArray[1], 'hex');
    const decipher = createDecipheriv('aes-256-ctr', Buffer.from(privateKey), iv);
    let decrypted = decipher.update(encryptedData);
    decrypted = Buffer.concat([decrypted, decipher.final()]);
    const decryptedText = decrypted.toString();
    //console.log(decryptedText);
    return JSON.parse(decryptedText);
  } catch (error) {
    console.error('Error desencriptando:', error);
    throw error;
  }
}

/**
 * The function `getTokenView` takes a token as input and returns the result of validating the token.
 * @param token - The `token` parameter is a string that represents a token.
 * @returns the result of the `validateToken` function.
 */
export function getTokenView(token) {
  return validateToken(token);
}
/**
 * The function encryptedaes256cbc takes in data and a private key, and encrypts the data using
 * AES-256-CBC encryption with a randomly generated initialization vector (IV) and the private key.
 * @param data - The `data` parameter is the data that you want to encrypt. It can be any type of data,
 * but it will be converted to a JSON string before encryption.
 * @param privateKey - The `privateKey` parameter is a secret key used for encryption. It should be a
 * string or a buffer containing the key value.
 * @returns the encrypted text in the format of `${iv.toString('hex')}:${encrypted.toString('hex')}`.
 */
export function encryptedaes256cbc(data, privateKey) {
  try {
    const iv = randomBytes(16);
    // console.log(iv.toString("hex"), data, privateKey);
    const cipher = createCipheriv('aes-256-ctr', Buffer.from(privateKey), iv);
    let encrypted = cipher.update(JSON.stringify(data));
    encrypted = Buffer.concat([encrypted, cipher.final()]);
    const encryptedText = `${iv.toString('hex')}:${encrypted.toString('hex')}`;

    return encryptedText;
  } catch (error) { }
}

/**
 * The function encrypts a plain text using AES-256-CBC encryption with a given private key.
 * @param data - The `data` parameter is the plain text that you want to encrypt using AES-256-CBC
 * encryption algorithm. It can be any string or binary data that you want to keep secure.
 * @param privateKey - The `privateKey` parameter is a secret key used for encryption. It should be a
 * string or a buffer containing the private key data.
 * @returns the encrypted text in the format of `${iv.toString('hex')}:${encrypted.toString('hex')}`.
 */
export function encryptedaes256cbcTextPlain(data, privateKey) {
  try {
    const iv = randomBytes(16);
    const cipher = createCipheriv('aes-256-ctr', Buffer.from(privateKey), iv);
    let encrypted = cipher.update(data);
    encrypted = Buffer.concat([encrypted, cipher.final()]);
    const encryptedText = `${iv.toString('hex')}:${encrypted.toString('hex')}`;
    return encryptedText;
  } catch (error) { }
}

/**
 * The function `validateToken` takes a token as input and returns a substring of the token by skipping
 * every 21st character.
 * @param token - The `token` parameter is a string that represents a token.
 * @returns the value of the variable `des`.
 */
export function validateToken(token) {
  if (token) {
    let b = 0;
    let des = '';
    try {
      let dat = token;
      while (b < dat.length - 20) {
        des = des + dat.charAt(b);
        b = b + 21;
      }
      return des;
    } catch (error) { }
  }
}

/**
 * The function checks if the user is active by decrypting a value stored in local storage and
 * returning true if the decrypted value has a "private" property, otherwise it returns false.
 * @returns a boolean value. It returns true if the condition `local.private` is truthy, and false
 * otherwise.
 */
export function IsUserActive() {
  const ceaData = localStorage.getItem('cea');
  if (ceaData) {
    try {
      // Primero intentar desencriptar
      const localToken = getTokenView(localStorage.getItem('session'));
      if (localToken) {
        try {
          let descrip = descryptedaes256cbc(ceaData, localToken);
          if (descrip && (descrip.private || descrip.access_token)) {
            return true;
          }
        } catch (decryptError) {
          // Si falla desencriptar, intentar JSON (compatibilidad hacia atrás)
          try {
            let local = JSON.parse(ceaData);
            if (local && (local.private || local.access_token)) {
              return true;
            }
          } catch (jsonError) {
            return false;
          }
        }
      } else {
        // Si no hay localToken, intentar JSON directamente
        try {
          let local = JSON.parse(ceaData);
          if (local && (local.private || local.access_token)) {
            return true;
          }
        } catch (jsonError) {
          return false;
        }
      }
    } catch (error) {
      return false;
    }
  }
  return false;
}

/**
 * The function `UserLoginData` retrieves and decrypts user login data from local storage, returning
 * the display email if available.
 * @returns the value of `local.displayEmail` if `localStorage.getItem('cea')` exists and
 * `local.private` is truthy. Otherwise, it returns an empty string.
 */
export function UserLoginData() {
  const ceaData = localStorage.getItem('cea');
  if (ceaData) {
    try {
      // Primero intentar desencriptar
      const localToken = getTokenView(localStorage.getItem('session'));
      if (localToken) {
        try {
          let descrip = descryptedaes256cbc(ceaData, localToken);
          if (descrip && (descrip.private || descrip.access_token)) {
            return descrip.displayEmail || '';
          }
        } catch (decryptError) {
          // Si falla desencriptar, intentar JSON (compatibilidad hacia atrás)
          try {
            let local = JSON.parse(ceaData);
            if (local && (local.private || local.access_token)) {
              return local.displayEmail || '';
            }
          } catch (jsonError) {
            return '';
          }
        }
      } else {
        // Si no hay localToken, intentar JSON directamente
        try {
          let local = JSON.parse(ceaData);
          if (local && (local.private || local.access_token)) {
            return local.displayEmail || '';
          }
        } catch (jsonError) {
          return '';
        }
      }
    } catch (error) {
      return '';
    }
  }
  return '';
}

export function UserData() {
  const ceaData = localStorage.getItem('cea');
  if (ceaData) {
    try {
      // Primero intentar desencriptar
      const localToken = getTokenView(localStorage.getItem('session'));
      if (localToken) {
        try {
          let descrip = descryptedaes256cbc(ceaData, localToken);
          if (descrip && (descrip.private || descrip.access_token)) {
            return descrip;
          }
        } catch (decryptError) {
          // Si falla desencriptar, intentar JSON (compatibilidad hacia atrás)
          try {
            let local = JSON.parse(ceaData);
            if (local && (local.private || local.access_token)) {
              return local;
            }
          } catch (jsonError) {
            return '';
          }
        }
      } else {
        // Si no hay localToken, intentar JSON directamente
        try {
          let local = JSON.parse(ceaData);
          if (local && (local.private || local.access_token)) {
            return local;
          }
        } catch (jsonError) {
          return '';
        }
      }
    } catch (error) {
      return '';
    }
  }
  return '';
}

/**
 * Obtiene el access token del localStorage
 */
function getToken() {
  let token = '';
  try {
    const ceaData = localStorage.getItem('cea');
    if (!ceaData) {
      return token;
    }

    // Primero intentar desencriptar
    const localToken = getTokenView(localStorage.getItem('session'));
    if (localToken) {
      try {
        const decrypted = descryptedaes256cbc(ceaData, localToken);
        if (decrypted && decrypted.access_token) {
          return decrypted.access_token;
        }
      } catch (decryptError) {
        // Si falla desencriptar, intentar JSON (compatibilidad hacia atrás)
        try {
          const tokenData = JSON.parse(ceaData);
          if (tokenData && tokenData.access_token) {
            return tokenData.access_token;
          }
        } catch (jsonError) {
          console.error("Error obteniendo token:", jsonError);
        }
      }
    } else {
      // Si no hay localToken, intentar JSON directamente
      try {
        const tokenData = JSON.parse(ceaData);
        if (tokenData && tokenData.access_token) {
          return tokenData.access_token;
        }
      } catch (jsonError) {
        console.error("Error obteniendo token:", jsonError);
      }
    }
  } catch (error) {
    console.error("Error obteniendo token:", error);
  }
  return token;
}

/**
 * Obtiene el companyId del usuario desde el JWT decodificado
 */
export function getCompanyId() {
  try {
    const token = getToken();
    if (!token) return null;
    
    // Decodificar el JWT (solo la parte del payload, sin verificar firma)
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
    
    const payload = JSON.parse(jsonPayload);
    return payload.companyId || null;
  } catch (error) {
    console.error('Error obteniendo companyId:', error);
    return null;
  }
}

/**
 * Obtiene el userId del usuario autenticado desde el JWT (`sub`).
 */
export function getUserId() {
  try {
    const token = getToken();
    if (!token) return null;

    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(function (c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join(''),
    );

    const payload = JSON.parse(jsonPayload);
    const userId = Number(payload.sub);
    return Number.isFinite(userId) ? userId : null;
  } catch (error) {
    console.error('Error obteniendo userId:', error);
    return null;
  }
}

/**
 * Obtiene is_superadmin del usuario desde el JWT decodificado
 */
export function isSuperAdmin() {
  try {
    const token = getToken();
    if (!token) return false;
    
    // Decodificar el JWT (solo la parte del payload, sin verificar firma)
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
    
    const payload = JSON.parse(jsonPayload);
    return payload.is_superadmin === true;
  } catch (error) {
    console.error('Error obteniendo is_superadmin:', error);
    return false;
  }
}

/**
 * Admin del hospedaje (o superadmin). Usuarios limitados retornan false.
 */
export function isAdmin() {
  try {
    if (isSuperAdmin()) return true;
    const token = getToken();
    if (!token) return false;

    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(function (c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join(''),
    );

    const payload = JSON.parse(jsonPayload);
    return payload.is_admin === true;
  } catch (error) {
    console.error('Error obteniendo is_admin:', error);
    return false;
  }
}

/**
 * Obtiene el companyId seleccionado por el superadmin (si existe)
 */
export function getSelectedCompanyId() {
  return localStorage.getItem('selectedCompanyId') ? parseInt(localStorage.getItem('selectedCompanyId')) : null;
}

/**
 * Guarda el companyId seleccionado por el superadmin
 */
export function setSelectedCompanyId(companyId) {
  if (companyId) {
    localStorage.setItem('selectedCompanyId', companyId.toString());
  } else {
    localStorage.removeItem('selectedCompanyId');
  }
}

/**
 * Obtiene el companyId a usar en las peticiones API
 * Si es superadmin y tiene una empresa seleccionada, usa esa
 * Si no, usa el companyId del usuario
 */
export function getApiCompanyId() {
  if (isSuperAdmin()) {
    const selected = getSelectedCompanyId();
    return selected || null;
  }
  return getCompanyId();
}





